import { Edit as EditIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import { portfolioApi } from "../../services/portfolioApi";

interface BulkEditPortfolioItemsButtonProps {
  portfolioId: string;
  selectedItems: Array<{
    id: string;
    name: string;
    type: string;
    notes?: string;
    labels?: string[];
    pin_rank?: number;
  }>;
  onSuccess?: () => void;
}

const BulkEditPortfolioItemsButton: React.FC<
  BulkEditPortfolioItemsButtonProps
> = ({ portfolioId, selectedItems, onSuccess }) => {
  const { showNotification } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form states
  const [notes, setNotes] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [pinRank, setPinRank] = useState<number | "">("");
  const [updateMode, setUpdateMode] = useState<"replace" | "append">("replace");

  const handleOpen = () => {
    // Reset form
    setNotes("");
    setLabels([]);
    setPinRank("");
    setUpdateMode("replace");
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleBulkEdit = async () => {
    if (!portfolioId || selectedItems.length === 0) return;

    try {
      setLoading(true);

      // Extract item IDs for bulk update (convert to integers as backend expects)
      const itemIds = selectedItems.map((item) => parseInt(item.id, 10));

      // Prepare updates object - only include fields that have values
      const updates: any = {};

      if (notes.trim()) {
        updates.notes = notes.trim();
      }

      if (labels.length > 0) {
        if (updateMode === "append") {
          // For append mode, we'd need to get current labels and merge
          // For now, we'll just replace - this could be enhanced later
          updates.labels = labels;
        } else {
          updates.labels = labels;
        }
      }

      if (pinRank !== "") {
        updates.pin_rank = Number(pinRank);
      }

      // Only proceed if there are updates to make
      if (Object.keys(updates).length === 0) {
        showNotification(
          "Please specify at least one field to update",
          "warning"
        );
        return;
      }

      await portfolioApi.bulkUpdateItems(portfolioId, {
        item_ids: itemIds,
        updates,
      });

      showNotification(
        `Successfully updated ${selectedItems.length} item${selectedItems.length !== 1 ? "s" : ""}`,
        "success"
      );

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to update portfolio items. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<EditIcon />}
        variant="outlined"
        size="small"
        disabled={selectedItems.length === 0}
        onClick={handleOpen}
      >
        Edit ({selectedItems.length})
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          Bulk Edit {selectedItems.length} Portfolio Items
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <Alert severity="info">
              Update the fields below. Only fields with values will be modified.
              Empty fields will be left unchanged.
            </Alert>

            {/* Selected items summary */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Items to be updated ({selectedItems.length}):
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxHeight: 120,
                  overflowY: "auto",
                }}
              >
                {selectedItems.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>

            {/* Notes field */}
            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Enter notes (leave empty to keep existing notes unchanged)"
              helperText="This will replace existing notes for all selected items"
            />

            {/* Labels field */}
            <Box>
              <Autocomplete
                multiple
                freeSolo
                value={labels}
                onChange={(_, newValue) => setLabels(newValue)}
                options={[]} // Could be populated with existing labels from the portfolio
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Labels"
                    placeholder="Add labels (press Enter to add)"
                    helperText="This will replace existing labels for all selected items"
                  />
                )}
              />
            </Box>

            {/* Pin Rank field */}
            <TextField
              fullWidth
              label="Pin Rank"
              type="number"
              value={pinRank}
              onChange={(e) =>
                setPinRank(e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="Enter pin rank (leave empty to keep existing rank unchanged)"
              helperText="Higher numbers appear first. Use 0 to unpin items."
              inputProps={{ min: 0, max: 999 }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleBulkEdit}
            variant="contained"
            disabled={selectedItems.length === 0 || loading}
          >
            {loading
              ? "Updating..."
              : `Update ${selectedItems.length} Item${selectedItems.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkEditPortfolioItemsButton;
